import React, { useState, useEffect } from 'react';
import { supabaseAdmin } from '../../../lib/supabaseAdmin';
import { Edit2, Save, X, CheckCircle, XCircle, AlertCircle, TrendingUp, Plus, Trash2, Code } from 'lucide-react';

interface GTMClasses {
  [bankId: string]: string;
}

interface PageConversionTracking {
  id: string;
  page_slug: string;
  page_name: string;
  conversion_label: string;
  is_active: boolean;
  description: string;
  button_selector?: string;
  gtm_classes?: GTMClasses;
  enable_gtm?: boolean;
  created_at: string;
  updated_at: string;
}

const ConversionTracking = () => {
  const [trackings, setTrackings] = useState<PageConversionTracking[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<PageConversionTracking>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newPageForm, setNewPageForm] = useState<Partial<PageConversionTracking>>({
    page_slug: '',
    page_name: '',
    conversion_label: '',
    is_active: true,
    description: '',
    button_selector: '',
    gtm_classes: {},
    enable_gtm: false,
  });
  const [editingGTM, setEditingGTM] = useState(false);
  const [gtmForm, setGtmForm] = useState<{ bankId: string; className: string }[]>([]);

  useEffect(() => {
    fetchTrackings();
  }, []);

  const fetchTrackings = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabaseAdmin
        .from('page_conversion_tracking')
        .select('*')
        .order('page_name', { ascending: true });

      if (error) throw error;
      setTrackings(data || []);
    } catch (error) {
      console.error('Error fetching conversion trackings:', error);
      setErrorMessage('Veriler yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (tracking: PageConversionTracking) => {
    setEditingId(tracking.id);
    setEditForm(tracking);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSave = async (id: string) => {
    try {
      const { error } = await supabaseAdmin
        .from('page_conversion_tracking')
        .update({
          conversion_label: editForm.conversion_label,
          is_active: editForm.is_active,
          description: editForm.description,
          button_selector: editForm.button_selector,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (error) throw error;

      setSuccessMessage('Sayfa tracking başarıyla güncellendi');
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditingId(null);
      setEditForm({});
      await fetchTrackings();
    } catch (error) {
      console.error('Error updating tracking:', error);
      setErrorMessage('Güncelleme sırasında hata oluştu');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const handleAddPage = async () => {
    try {
      if (!newPageForm.page_slug || !newPageForm.page_name || !newPageForm.conversion_label) {
        setErrorMessage('Lütfen tüm zorunlu alanları doldurun');
        setTimeout(() => setErrorMessage(null), 3000);
        return;
      }

      const { error } = await supabaseAdmin
        .from('page_conversion_tracking')
        .insert([{
          page_slug: newPageForm.page_slug,
          page_name: newPageForm.page_name,
          conversion_label: newPageForm.conversion_label,
          is_active: newPageForm.is_active,
          description: newPageForm.description,
          button_selector: newPageForm.button_selector,
        }]);

      if (error) throw error;

      setSuccessMessage('Yeni sayfa tracking eklendi');
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowAddModal(false);
      setNewPageForm({
        page_slug: '',
        page_name: '',
        conversion_label: '',
        is_active: true,
        description: '',
        button_selector: '',
      });
      await fetchTrackings();
    } catch (error) {
      console.error('Error adding tracking:', error);
      setErrorMessage('Ekleme sırasında hata oluştu');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Bu sayfa tracking\'ini silmek istediğinize emin misiniz?')) {
      return;
    }

    try {
      const { error } = await supabaseAdmin
        .from('page_conversion_tracking')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setSuccessMessage('Sayfa tracking silindi');
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchTrackings();
    } catch (error) {
      console.error('Error deleting tracking:', error);
      setErrorMessage('Silme sırasında hata oluştu');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  const toggleActive = async (tracking: PageConversionTracking) => {
    try {
      const { error } = await supabaseAdmin
        .from('page_conversion_tracking')
        .update({
          is_active: !tracking.is_active,
          updated_at: new Date().toISOString(),
        })
        .eq('id', tracking.id);

      if (error) throw error;

      setSuccessMessage(
        `Tracking ${!tracking.is_active ? 'aktif' : 'pasif'} edildi`
      );
      setTimeout(() => setSuccessMessage(null), 3000);
      await fetchTrackings();
    } catch (error) {
      console.error('Error toggling tracking:', error);
      setErrorMessage('İşlem sırasında hata oluştu');
      setTimeout(() => setErrorMessage(null), 5000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F7BA45] mx-auto mb-2"></div>
          <p className="text-sm text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-7 w-7 text-[#F7BA45]" />
            <div>
              <h1 className="text-2xl font-bold text-slate-800">
                Sayfa Bazlı Conversion Tracking
              </h1>
              <p className="text-slate-600 text-sm">
                Her sayfa için ayrı Google Ads dönüşüm etiketleri yönetin
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 rounded-lg transition-colors font-medium"
          >
            <Plus className="h-5 w-5" />
            Yeni Sayfa Ekle
          </button>
        </div>
      </div>

      {/* Alert Messages */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-800">{successMessage}</span>
        </div>
      )}

      {errorMessage && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <XCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{errorMessage}</span>
        </div>
      )}

      {/* Info Box */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Conversion Label Nasıl Bulunur?</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Google Ads → Tools & Settings → Conversions</li>
              <li>İlgili conversion action'ı seçin</li>
              <li>Tag setup → Event snippet</li>
              <li>"send_to" değerindeki slash sonrası kısmı kopyalayın</li>
              <li>Örnek: AW-123456/<strong>AbC-D_efG-h_12-345</strong></li>
            </ol>
          </div>
        </div>
      </div>

      {/* Page Tracking Cards */}
      <div className="grid gap-4">
        {trackings.map((tracking) => {
          const isEditing = editingId === tracking.id;

          return (
            <div
              key={tracking.id}
              className="bg-slate-50 rounded-lg border border-slate-200 p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-slate-800">
                      {tracking.page_name}
                    </h3>
                    <button
                      onClick={() => toggleActive(tracking)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        tracking.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {tracking.is_active ? 'Aktif' : 'Pasif'}
                    </button>
                  </div>
                  <p className="text-sm text-slate-600 font-mono mb-1">
                    {tracking.page_slug}
                  </p>
                  {tracking.description && (
                    <p className="text-sm text-slate-600">{tracking.description}</p>
                  )}
                </div>
                {!isEditing && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(tracking)}
                      className="p-2 text-slate-600 hover:text-[#F7BA45] hover:bg-white rounded-lg transition-colors"
                    >
                      <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(tracking.id)}
                      className="p-2 text-slate-600 hover:text-red-600 hover:bg-white rounded-lg transition-colors"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>

              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Conversion Label <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.conversion_label || ''}
                      onChange={(e) =>
                        setEditForm({ ...editForm, conversion_label: e.target.value })
                      }
                      placeholder="AbC-D_efG-h_12-345"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Açıklama
                    </label>
                    <textarea
                      value={editForm.description || ''}
                      onChange={(e) =>
                        setEditForm({ ...editForm, description: e.target.value })
                      }
                      rows={2}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Buton Selector (CSS) - Opsiyonel
                    </label>
                    <input
                      type="text"
                      value={editForm.button_selector || ''}
                      onChange={(e) =>
                        setEditForm({ ...editForm, button_selector: e.target.value })
                      }
                      placeholder=".apply-button, a[href*='basvuru']"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`active-${tracking.id}`}
                      checked={editForm.is_active || false}
                      onChange={(e) =>
                        setEditForm({ ...editForm, is_active: e.target.checked })
                      }
                      className="w-4 h-4 text-[#F7BA45] border-slate-300 rounded focus:ring-[#F7BA45]"
                    />
                    <label
                      htmlFor={`active-${tracking.id}`}
                      className="text-sm text-slate-700"
                    >
                      Tracking'i aktif et
                    </label>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => handleSave(tracking.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 rounded-lg transition-colors font-medium"
                    >
                      <Save className="h-4 w-4" />
                      Kaydet
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-lg transition-colors border border-slate-300"
                    >
                      <X className="h-4 w-4" />
                      İptal
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-slate-200">
                    <div className="text-xs text-slate-500 mb-1">
                      Conversion Label
                    </div>
                    <div className="font-mono text-sm text-slate-800 break-all">
                      {tracking.conversion_label}
                    </div>
                  </div>

                  {tracking.button_selector && (
                    <div className="bg-white rounded-lg p-3 border border-slate-200">
                      <div className="text-xs text-slate-500 mb-1">
                        Buton Selector
                      </div>
                      <div className="font-mono text-sm text-slate-800">
                        {tracking.button_selector}
                      </div>
                    </div>
                  )}

                  <div className="text-xs text-slate-500">
                    Son güncelleme:{' '}
                    {new Date(tracking.updated_at).toLocaleString('tr-TR')}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {trackings.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500">Henüz sayfa tracking eklenmemiş</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 text-[#F7BA45] hover:underline"
            >
              İlk sayfa tracking'ini ekleyin
            </button>
          </div>
        )}
      </div>

      {/* Add New Page Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-slate-800 mb-4">
              Yeni Sayfa Tracking Ekle
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Sayfa Yolu (Slug) <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPageForm.page_slug || ''}
                  onChange={(e) =>
                    setNewPageForm({ ...newPageForm, page_slug: e.target.value })
                  }
                  placeholder="/uzaktan-musteri-edinimi"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Sayfa Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPageForm.page_name || ''}
                  onChange={(e) =>
                    setNewPageForm({ ...newPageForm, page_name: e.target.value })
                  }
                  placeholder="Uzaktan Müşteri Edinimi"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Conversion Label <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newPageForm.conversion_label || ''}
                  onChange={(e) =>
                    setNewPageForm({ ...newPageForm, conversion_label: e.target.value })
                  }
                  placeholder="AbC-D_efG-h_12-345"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Açıklama
                </label>
                <textarea
                  value={newPageForm.description || ''}
                  onChange={(e) =>
                    setNewPageForm({ ...newPageForm, description: e.target.value })
                  }
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Buton Selector (CSS) - Opsiyonel
                </label>
                <input
                  type="text"
                  value={newPageForm.button_selector || ''}
                  onChange={(e) =>
                    setNewPageForm({ ...newPageForm, button_selector: e.target.value })
                  }
                  placeholder=".apply-button, a[href*='basvuru']"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-transparent"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="new-active"
                  checked={newPageForm.is_active || false}
                  onChange={(e) =>
                    setNewPageForm({ ...newPageForm, is_active: e.target.checked })
                  }
                  className="w-4 h-4 text-[#F7BA45] border-slate-300 rounded focus:ring-[#F7BA45]"
                />
                <label htmlFor="new-active" className="text-sm text-slate-700">
                  Tracking'i aktif et
                </label>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <button
                onClick={handleAddPage}
                className="flex items-center gap-2 px-4 py-2 bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 rounded-lg transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                Ekle
              </button>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewPageForm({
                    page_slug: '',
                    page_name: '',
                    conversion_label: '',
                    is_active: true,
                    description: '',
                    button_selector: '',
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-lg transition-colors border border-slate-300"
              >
                <X className="h-4 w-4" />
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversionTracking;
